import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { RefreshCw, ArrowUp } from 'lucide-react';
import { getKmfcClient } from '../lib/supabase';
import packageJson from '../../package.json';
import DataSyncService from '../kmfc-shop/services/DataSyncService';

const SOUND_KEY  = 'kmfc-sound-enabled';
const CHIME_FILE = '/assets/chime.wav';

const versionChimeLoop = (() => {
  let audio = null;
  let active = false;
  let timer = null;

  function isEnabled() {
    try { return JSON.parse(localStorage.getItem(SOUND_KEY) || 'false'); } catch { return false; }
  }

  function playNext() {
    if (!active || !isEnabled()) return;
    audio = new Audio(CHIME_FILE);
    audio.volume = 1.0;
    audio.play().catch(() => {});
    audio.onended = () => { timer = setTimeout(playNext, 2000); };
  }

  return {
    start() {
      if (active || !isEnabled()) return;
      active = true;
      playNext();
    },
    stop() {
      active = false;
      if (timer) { clearTimeout(timer); timer = null; }
      if (audio) { audio.pause(); audio.onended = null; audio = null; }
    },
  };
})();

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.94) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 32px 28px 24px;
  max-width: 380px;
  width: calc(100% - 32px);
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.5);
  animation: ${scaleIn} 0.25s cubic-bezier(0.16, 1, 0.3, 1) both;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(249, 115, 22, 0.12);
  border: 1px solid rgba(249, 115, 22, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f97316;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0 0 24px;
  line-height: 1.6;
`;

const VersionRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  margin-bottom: 24px;
`;

const VersionBox = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  padding: 10px 12px;
  text-align: left;
`;

const VersionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const VersionValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.$old ? '#f97316' : 'rgba(255,255,255,0.9)'};
  font-variant-numeric: tabular-nums;
`;

const RefreshBtn = styled.button`
  width: 100%;
  padding: 13px;
  border-radius: 14px;
  border: none;
  background: #f97316;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }

  &[data-loading="true"] svg { animation: ${spin} 0.8s linear infinite; }
`;

const IgnoreBtn = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 11px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(255,255,255,0.22); color: rgba(255,255,255,0.55); }
`;

const WarningMsg = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.18);
  font-size: 12px;
  color: #fca5a5;
  line-height: 1.5;
  text-align: left;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 8px;
  text-align: left;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: #f97316;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  line-height: 1.4;
`;

const IgnoreConfirmBtn = styled.button`
  width: 100%;
  margin-top: 4px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  background: rgba(239, 68, 68, 0.08);
  color: #f87171;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.disabled ? 0.4 : 1};
  transition: background 0.15s, opacity 0.15s;
  &:hover:not(:disabled) { background: rgba(239, 68, 68, 0.15); }
`;

const VERSION_POLL_MS = 12 * 60 * 1000;

export default function VersionGuard({ children }) {
  const [blocked, setBlocked] = useState(false);
  const [versions, setVersions] = useState({ local: '', remote: '' });
  const [ignoreStep, setIgnoreStep] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const isInitialRef = useRef(true);

  const [confirmed, setConfirmed] = useState(false);

  const check = useCallback(async () => {
    try {
      const client = getKmfcClient();
      if (!client) return;

      const { data, error } = await client
        .from('shop_settings')
        .select('Mijn_webvonk_website_versie')
        .eq('id', 1)
        .single();

      if (error || !data) return;

      const remoteVersion = data['Mijn_webvonk_website_versie'];
      const localVersion = packageJson.version;

      if (!remoteVersion) return;

      if (window.location.hostname === 'localhost') return;

      if (remoteVersion !== localVersion) {
        setVersions({ local: localVersion, remote: remoteVersion });
        setBlocked(true);
      } else {
        setBlocked(false);
      }
    } catch {
      // Geen DB bereikbaar — gewoon doorgaan
    } finally {
      isInitialRef.current = false;
    }
  }, []);

  useEffect(() => {
    check();
    const interval = setInterval(check, VERSION_POLL_MS);
    return () => clearInterval(interval);
  }, [check]);

  useEffect(() => {
    const unsub = DataSyncService.subscribe('webvonkUpdate', ({ from, to }) => {
      if (window.location.hostname === 'localhost') return;
      setVersions({ local: from, remote: to });
      setBlocked(true);
      versionChimeLoop.start();
    });
    return unsub;
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => window.location.reload(), 400);
  };

  const handleBypass = () => {
    setBlocked(false);
  };

  return (
    <>
      {blocked && (
        <Overlay>
          <Modal>
            <IconWrap><ArrowUp size={28} /></IconWrap>
            <Title>Update vereist</Title>
            <Subtitle>
              Er is een nieuwe versie beschikbaar. Ververs de pagina om door te gaan.
            </Subtitle>

            <VersionRow>
              <VersionBox>
                <VersionLabel>Jouw versie</VersionLabel>
                <VersionValue $old>v{versions.local}</VersionValue>
              </VersionBox>
              <VersionBox>
                <VersionLabel>Nieuwe versie</VersionLabel>
                <VersionValue>v{versions.remote}</VersionValue>
              </VersionBox>
            </VersionRow>

            <RefreshBtn onClick={handleRefresh} data-loading={refreshing}>
              <RefreshCw size={16} />
              {refreshing ? 'Verversen...' : 'Pagina verversen'}
            </RefreshBtn>

            {ignoreStep === 0 ? (
              <IgnoreBtn onClick={() => setIgnoreStep(1)}>Negeer melding</IgnoreBtn>
            ) : (
              <>
                <WarningMsg>Weet u het zeker? Een verouderde versie kan problemen opleveren.</WarningMsg>
                <CheckboxRow>
                  <CheckboxInput
                    type="checkbox"
                    id="ignore-confirm"
                    checked={confirmed}
                    onChange={e => setConfirmed(e.target.checked)}
                  />
                  <CheckboxLabel htmlFor="ignore-confirm">Ik wil de update overslaan</CheckboxLabel>
                </CheckboxRow>
                <IgnoreConfirmBtn onClick={handleBypass} disabled={!confirmed}>
                  Doorgaan zonder update
                </IgnoreConfirmBtn>
                <IgnoreBtn onClick={() => { setIgnoreStep(0); setConfirmed(false); }} style={{ marginTop: 6, fontSize: 12 }}>
                  Toch verversen
                </IgnoreBtn>
              </>
            )}
          </Modal>
        </Overlay>
      )}

      {children}
    </>
  );
}
